import type { ErpSourceSystem } from '@siliconx/shared';

/**
 * Per-ERP brand iconography (SIL-270, W4-T10 of
 * resources/plans/erp-register-import-spec.md decision 6/7).
 *
 * Mirrors `components/shared/ManufacturerLogo.tsx`'s dual-theme approach —
 * that component wraps dark-marked logos in a literal (inline-style, not
 * `bg-white`) white chip so the mark stays legible under `.dark` (this app
 * remaps `bg-white`/`border-zinc-*` to `--n-*` CSS vars that flip to
 * near-black in dark mode — a literal chip bypasses that remap). Our four
 * brand marks are new SVGs authored for this ticket (no upstream asset to
 * mirror), so instead of a blocklist-driven chip we bake a solid brand-color
 * background straight into every mark (`public/erp-logos/*.svg`) — the same
 * "own opaque chip, independent of page theme" outcome, applied uniformly
 * rather than case-by-case.
 */

export const ERP_SOURCE_SYSTEMS_UI: readonly { id: ErpSourceSystem; label: string }[] = [
  { id: 'netsuite', label: 'NetSuite' },
  { id: 'sap', label: 'SAP' },
  { id: 'dynamics_365', label: 'Microsoft Dynamics 365' },
  { id: 'sage_intacct', label: 'Sage Intacct' },
];

export function erpSourceSystemLabel(id: ErpSourceSystem | null | undefined): string {
  return ERP_SOURCE_SYSTEMS_UI.find((s) => s.id === id)?.label ?? 'Unknown ERP';
}

export function ErpBrandIcon({
  system,
  size = 20,
  className = '',
}: {
  system: ErpSourceSystem;
  /** icon height/width in px — the mark is a fixed 1:1 rounded chip. */
  size?: number;
  className?: string;
}) {
  return (
    <img
      src={`/erp-logos/${system}.svg`}
      alt={erpSourceSystemLabel(system)}
      width={size}
      height={size}
      className={`inline-block shrink-0 rounded-md ${className}`}
    />
  );
}
